import { useState, Fragment, useEffect, useMemo } from 'react';
import { useSession } from '@supabase/auth-helpers-react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthRedirect } from '../hooks/useAuthRedirect';
import { PlusIcon, XMarkIcon, ExclamationTriangleIcon, PencilSquareIcon, ChevronRightIcon, ArrowTopRightOnSquareIcon, ArrowDownTrayIcon, ChevronDownIcon, Squares2X2Icon, ListBulletIcon, PrinterIcon, ClipboardDocumentListIcon, CalendarDaysIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, Transition, Listbox } from '@headlessui/react';
import { supabase } from '../lib/supabase';
import AddChildModal from '../components/AddChildModal';
import CalendarExport from '../components/CalendarExport';
import CalendarSubscription from '../components/CalendarSubscription';
import CalendarShareModal from '../components/CalendarShareModal';
import CalendarPreviewModal from '../components/CalendarPreviewModal';
import { generateBulkICSFile } from '../components/BulkCalendarExport';
import toast from 'react-hot-toast';
import AddToCalendarModal from '../components/AddToCalendarModal';
import { Tooltip } from '../components/ui/Tooltip';
import { track } from '../lib/track';
import SEO from '../components/SEO';
import { calculateCurrentAge, formatAge } from '../lib/ageCalculation';
import { useUserTypes } from '../hooks/useUserTypes';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { getYearFromDateString, getTodayDateString, daysBetween, formatDateString, parseDateString } from '../lib/dateUtils';
import LoadingState from '../components/LoadingState';

interface Child {
  id: string;
  name: string;
  date_of_birth: string | null;
  age?: number; // Optional, kept for backward compatibility during transition
  camp_schedules?: CampSchedule[];
}

interface CampSchedule {
  id: string;
  camp_id: string;
  child_id?: string;
  start_date: string;
  end_date: string;
  event_year?: number | null;
  camp?: {
    id: string;
    name: string;
    description?: string;
    address?: string;
    city?: string;
    state?: string;
    zip_code?: string;
  };
}

interface DeleteConfirmation {
  scheduleId: string;
  childName: string;
  campName: string;
  camp_id?: string;
  child_id?: string;
}

// Add type for the child form data
interface ChildFormData {
  id: string;
  name: string;
  date_of_birth: string; // YYYY-MM-DD format
}

// Add type for API error
interface ApiError {
  message: string;
}

// Add this type guard function
const ensureString = (value: string | null): string => {
  return value ?? '';  // Returns empty string if value is null
};

export default function KidsSchedule(): JSX.Element {
  const session = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  // Get user types from the user_types table
  const { userTypes, isParent } = useUserTypes();

  // Handle authentication redirect with proper session initialization
  const { isChecking, shouldRender } = useAuthRedirect({
    redirectTo: '/login?redirect=/kids-schedule',
  });

  // Check if user is a camp owner and redirect if so
  useEffect(() => {
    if (session?.user && userTypes.includes('camp-owner') && !isParent) {
      // User is only a camp owner, not a parent - redirect to their camp
      navigate('/mycamp-teaser');
      return;
    }
  }, [session, userTypes, isParent, navigate]);
  
  // Simplify state management
  const [isAddChildModalOpen, setIsAddChildModalOpen] = useState(false);
  const [isAddScheduleModalOpen, setIsAddScheduleModalOpen] = useState(false);
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmation | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedChild, setSelectedChild] = useState<ChildFormData | null>(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ type: 'child' | 'camp'; id: string } | null>(null);
  const [editingSchedule, setEditingSchedule] = useState<{ scheduleId: string; campId: string; startDate: string; endDate: string } | null>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  // Year selector with localStorage persistence
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(() => {
    const saved = localStorage.getItem('campwhiz-selected-year');
    return saved ? parseInt(saved, 10) : currentYear;
  });

  // View mode toggle with localStorage persistence
  type ViewMode = 'child' | 'month';
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('campwhiz-calendar-view-mode');
    return (saved === 'month' || saved === 'child') ? saved : 'child';
  });

  // Save selected year to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('campwhiz-selected-year', selectedYear.toString());
  }, [selectedYear]);

  // Save view mode to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('campwhiz-calendar-view-mode', viewMode);
  }, [viewMode]);

  // Modify query to match favorites pattern
  const { 
    data: children = [], 
    error: childrenError, 
    isLoading,
    isFetching,
    refetch 
  } = useQuery<Child[], ApiError>(
    ['children', session?.user?.id], // Add user ID to query key
    async () => {
      if (!session) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('children')
        .select(`
          id,
          user_id,
          name,
          date_of_birth,
          created_at,
          updated_at,
          camp_schedules (
            id,
            camp_id,
            start_date,
            end_date,
            event_year,
            camp:camps (
              id,
              name,
              description,
              address,
              city,
              state,
              zip_code,
              slug,
              latitude,
              longitude
            )
          )
        `)
        .eq('user_id', session.user.id)
        .order('created_at');

      if (error) throw error;
      
      return data || [];
    },
    {
      enabled: !!session,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
      staleTime: 1000 * 60, // 1 minute
      cacheTime: 1000 * 60 * 5, // 5 minutes
      retry: 2
    }
  );

  // Extract available years from all schedules - AFTER useQuery but BEFORE early returns
  const availableYears = useMemo(() => {
    const years = new Set<number>();
    years.add(currentYear); // Always include current year
    years.add(currentYear + 1); // Always include next year for planning
    
    if (children && Array.isArray(children)) {
      children.forEach(child => {
        child.camp_schedules?.forEach(schedule => {
          if (schedule.event_year !== null && schedule.event_year !== undefined) {
            years.add(schedule.event_year);
          } else if (schedule.start_date) {
            const year = getYearFromDateString(schedule.start_date);
            if (year !== null) {
              years.add(year);
            }
          }
        });
      });
    }
    
    return Array.from(years).sort((a, b) => b - a); // Sort descending (newest first)
  }, [children, currentYear]);

  // Filter children's schedules by selected year - AFTER useQuery but BEFORE early returns
  // IMPORTANT: This includes ALL children, even if they have no schedules for the selected year
  // This is the core behavior: show all children, filter only their schedules by year
  // A child with no schedules for 2026 will still appear with "No camps scheduled" message
  const filteredChildren = useMemo(() => {
    if (!children || !Array.isArray(children)) {
      return [];
    }
    
    // Map over all children - don't filter out children, only filter their schedules
    // This ensures children without schedules for the selected year still appear
    const result = children.map(child => ({
      ...child,
      camp_schedules: (child.camp_schedules?.filter(schedule => {
        // Always use start_date as source of truth for consistent filtering
        const year = getYearFromDateString(schedule.start_date);
        return year !== null && year === selectedYear;
      }) || []).sort((a, b) => a.start_date.localeCompare(b.start_date)) // Sort by earliest date first
    }));
    
    return result;
  }, [children, selectedYear]);

  // Color palette for children (matching PublicCalendarView)
  const CHILD_COLORS = [
    '#3B82F6', // blue
    '#10B981', // green
    '#F59E0B', // amber
    '#EF4444', // red
    '#8B5CF6', // purple
    '#EC4899', // pink
    '#06B6D4', // cyan
    '#F97316', // orange
  ];

  // Assign colors to children
  const childColorMap = useMemo(() => {
    const map = new Map<string, string>();
    filteredChildren.forEach((child, index) => {
      map.set(child.id, CHILD_COLORS[index % CHILD_COLORS.length]);
    });
    return map;
  }, [filteredChildren]);

  // Group schedules by month for month view (similar to PublicCalendarView)
  const schedulesByMonth = useMemo(() => {
    const grouped: { [month: string]: (CampSchedule & { child_id: string; child_name: string })[] } = {};
    
    filteredChildren.forEach(child => {
      child.camp_schedules?.forEach(schedule => {
        const year = getYearFromDateString(schedule.start_date);
        if (year === null) return;
        
        // Extract month from date string (YYYY-MM-DD format)
        const month = schedule.start_date.split('-')[1];
        const monthKey = `${year}-${month}`;
        
        if (!grouped[monthKey]) {
          grouped[monthKey] = [];
        }
        grouped[monthKey].push({
          ...schedule,
          child_id: child.id,
          child_name: child.name,
        });
      });
    });

    return grouped;
  }, [filteredChildren]);


  // Show loading state while checking session (prevents flash of login page)
  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Return null if not authenticated (hook will handle redirect)
  if (!shouldRender) {
    return null;
  }

  // Show loading state only on initial load (not on refetch)
  if (isLoading && children.length === 0) {
    return (
      <div className="min-h-screen">
        <LoadingState message="Loading your children's schedules..." fullPage />
      </div>
    );
  }

  const handleAddSchedule = async (campId: string, startDate: string, endDate: string) => {
    if (!selectedChildId && !editingSchedule) return;

    try {
      // Calculate event_year from start_date (extract year from string)
      const eventYear = getYearFromDateString(startDate);
      if (eventYear === null) {
        throw new Error('Invalid date format');
      }
      
      const currentYear = new Date().getFullYear();
      const today = getTodayDateString();
      
      // Validation: allow dates in current year or future, or up to 30 days in the past for corrections
      if (eventYear < currentYear) {
        throw new Error(`Can't create camp schedules for ${eventYear}. Please select dates in ${currentYear} or later.`);
      }
      
      // Allow dates up to 30 days in the past for corrections
      const daysDiff = daysBetween(startDate, today);
      if (daysDiff < -30) {
        // Calculate min date (30 days ago) for error message
        const minDate = parseDateString(today);
        if (minDate) {
          minDate.setDate(minDate.getDate() - 30);
          const minDateStr = minDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
          throw new Error(`Can't create camp schedules more than 30 days in the past. Please select dates from ${minDateStr} or later.`);
        }
        throw new Error(`Can't create camp schedules more than 30 days in the past.`);
      }

      if (editingSchedule) {
        // Update existing schedule
        const { error: updateError } = await supabase
          .from('camp_schedules')
          .update({
            start_date: startDate,
            end_date: endDate,
            event_year: eventYear
          })
          .eq('id', editingSchedule.scheduleId);

        if (updateError) throw updateError;

        toast.success('Schedule updated successfully', {
          position: 'bottom-center'
        });
        setEditingSchedule(null);
      } else {
        // Insert new schedule
        const { error: insertError } = await supabase
          .from('camp_schedules')
          .insert({
            child_id: selectedChildId!,
            camp_id: campId,
            start_date: startDate,
            end_date: endDate,
            event_year: eventYear
          })
          .select(`
            id,
            camp_id,
            start_date,
            end_date,
            camp:camps!inner (
              id,
              name,
              description,
              address,
              city,
              state,
              zip_code
            )
          `);

        if (insertError) throw insertError;

        track('camp_added_to_schedule', {
          camp_id: campId,
          child_id: selectedChildId,
          persona: 'parent',
          event_year: eventYear,
          start_date: startDate,
          end_date: endDate
        });

        toast.success('Schedule added successfully', {
          position: 'bottom-center'
        });
      }

      // Immediately invalidate and refetch
      await Promise.all([
        queryClient.invalidateQueries(['children', session?.user?.id]),
        refetch()
      ]);

      setIsAddScheduleModalOpen(false);
      setSelectedChildId(null);
    } catch (error: any) {
      console.error('Error saving schedule:', error);
      const errorMessage = error?.message || (editingSchedule ? 'Failed to update schedule' : 'Failed to add schedule');
      toast.error(errorMessage, {
        position: 'bottom-center'
      });
      // Close modal on error so user can see the toast notification
      setIsAddScheduleModalOpen(false);
      setSelectedChildId(null);
    }
  };

  const handleEditSchedule = (schedule: CampSchedule) => {
    if (!schedule.camp?.id) return;
    setEditingSchedule({
      scheduleId: schedule.id,
      campId: schedule.camp.id,
      startDate: schedule.start_date,
      endDate: schedule.end_date
    });
    setSelectedChildId(schedule.child_id || null);
    setIsAddScheduleModalOpen(true);
  };

  const handleRemoveSchedule = async () => {
    if (!deleteConfirmation) return;

    try {
      const { error } = await supabase
        .from('camp_schedules')
        .delete()
        .eq('id', deleteConfirmation.scheduleId);

      if (error) throw error;

      track('camp_removed_from_schedule', {
        persona: 'parent',
        schedule_id: deleteConfirmation.scheduleId,
        camp_id: deleteConfirmation.camp_id,
        child_id: deleteConfirmation.child_id,
        child_name: deleteConfirmation.childName,
        camp_name: deleteConfirmation.campName,
      });

      await queryClient.invalidateQueries(['children', session?.user?.id]);
      await refetch();

      toast.success('Schedule removed successfully');
    } catch (error) {
      console.error('Error removing schedule:', error);
      toast.error('Failed to remove schedule');
    } finally {
      setDeleteConfirmation(null);
    }
  };

  const handleEditChild = async () => {
    if (!selectedChild || !selectedChild.id) return;

    try {
      const { error } = await supabase
        .from('children')
        .update({ 
          name: selectedChild.name, 
          date_of_birth: selectedChild.date_of_birth 
        })
        .eq('id', selectedChild.id);

      if (error) throw error;

      toast.success('Child updated successfully');
      setIsEditModalOpen(false);
      queryClient.invalidateQueries(['children', session?.user?.id]);
    } catch (error) {
      console.error('Error updating child:', error);
      toast.error('Failed to update child');
    }
  };

  const handleDeleteChild = async (id: string) => {
    try {
      const { error } = await supabase
        .from('children')
        .delete()
        .eq('id', id);

      if (error) throw error;

      track('child_deleted', {
        persona: 'parent',
        child_id: id,
      });

      toast.success('Child deleted successfully');
      queryClient.invalidateQueries(['children', session?.user?.id]);
    } catch (error) {
      console.error('Error deleting child:', error);
      toast.error('Failed to delete child');
    }
  };

  const handleDeleteCamp = (id: string) => {
    setItemToDelete({ type: 'camp', id });
    setIsDeleteConfirmationOpen(true);
  };

  const calculateChildAge = (dob: string) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    if (Number.isNaN(birthDate.getTime())) {
      return null;
    }

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const hasHadBirthdayThisYear =
      today.getMonth() > birthDate.getMonth() ||
      (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());

    if (!hasHadBirthdayThisYear) {
      age -= 1;
    }

    return Math.max(age, 0);
  };

  const handleAddChild = async (name: string, dateOfBirth: string) => {
    try {
      const { data: newChild, error } = await supabase
        .from('children')
        .insert({
          name,
          date_of_birth: dateOfBirth,
          user_id: session?.user?.id
        })
        .select(`
          *,
          camp_schedules (
            id,
            camp_id,
            start_date,
            end_date,
            camp:camps (
              id,
              name,
              description,
              address,
              city,
              state,
              zip_code
            )
          )
        `)
        .single();

      if (error) throw error;

      // Track child addition (PostHog + dataLayer for GTM/Meta)
      const childAge = calculateChildAge(dateOfBirth);
      track('child_added', {
        persona: 'parent',
        child_name: name,
        ...(childAge !== null ? { child_age: childAge } : {}),
        user_id: session?.user?.id,
        total_children: children.length + 1
      });

      // Immediately update the query cache with the new child
      if (newChild && session?.user?.id) {
        const queryKey: [string, string] = ['children', session.user.id];
        
        // Optimistically update the cache immediately
        queryClient.setQueryData(queryKey, (oldData: Child[] | undefined) => {
          // Prevent duplicates
          if (oldData?.some(c => c.id === newChild.id)) {
            console.log('[Calendar] Child already exists in cache, skipping');
            return oldData;
          }
          const updated = oldData ? [...oldData, newChild] : [newChild];
          console.log('[Calendar] Optimistically added child to cache:', { 
            childName: name, 
            childId: newChild.id, 
            totalChildren: updated.length,
            queryKey
          });
          return updated;
        });
      }
      
      // Force a fresh refetch from server to ensure data consistency
      if (session?.user?.id) {
        console.log('[Calendar] Invalidating and refetching children query...');
        // Invalidate first
        queryClient.invalidateQueries(['children', session.user.id]);
        // Then refetch - this ensures fresh data from server
        const refetchResult = await refetch();
        console.log('[Calendar] Refetch result:', { 
          isSuccess: refetchResult.isSuccess, 
          isError: refetchResult.isError,
          childrenCount: refetchResult.data?.length || 0,
          error: refetchResult.error,
          children: refetchResult.data?.map(c => ({ id: c.id, name: c.name })) || []
        });
      }

      toast.success('Child added successfully', {
        position: 'bottom-center'
      });
      setIsAddChildModalOpen(false);
    } catch (error) {
      console.error('Error adding child:', error);
      toast.error('Failed to add child', {
        position: 'bottom-center'
      });
    }
  };

  return (
    <>
      <SEO
        title="Kids Schedule | CampWhiz"
        description="Manage your children's summer camp schedules. Add kids, schedule camps, and export to your calendar. Plan the perfect summer for your family."
        keywords="kids schedule, summer camp planning, camp calendar, family schedule, camp management"
        url="/kids-schedule"
      />
      <div className="page-container" style={{ minHeight: 'auto' }}>
        <div className="page-content pb-12">
          <div className="mb-4 sm:mb-8 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold text-gray-900">
                <span className="inline-flex items-center gap-2">
                  <Listbox value={selectedYear} onChange={setSelectedYear}>
                    <div className="relative">
                      <Listbox.Button className="inline-flex items-center gap-1.5 text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold text-primary hover:text-primary/80 transition-colors focus:outline-none focus:ring-0">
                        <span>{selectedYear}</span>
                        <ChevronDownIcon className="h-6 w-6 text-primary" />
                      </Listbox.Button>
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <Listbox.Options className="absolute left-0 mt-2 w-32 max-h-60 overflow-auto rounded-lg bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                          {availableYears.map((year) => (
                            <Listbox.Option
                              key={year}
                              value={year}
                              className={({ active }) =>
                                `relative cursor-pointer select-none py-2 px-4 ${
                                  active ? 'bg-primary/10 text-primary' : 'text-gray-900'
                                } ${selectedYear === year ? 'bg-primary/20 font-medium' : ''}`
                              }
                            >
                              {year}
                            </Listbox.Option>
                          ))}
                        </Listbox.Options>
                      </Transition>
                    </div>
                  </Listbox>
                  Kids Schedule
                </span>
              </h1>
            </div>
          <div className="flex items-center gap-3">
            {children.length > 0 && (
              <>
                <button
                  onClick={() => {
                    track('calendar_preview_clicked', {
                      persona: 'parent',
                      year: selectedYear,
                      children_count: children.length,
                    });
                    setIsPreviewModalOpen(true);
                  }}
                  className="btn btn-secondary inline-flex items-center gap-1.5 text-sm sm:text-base"
                >
                  <PrinterIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="hidden sm:inline">Preview & Print</span>
                  <span className="sm:hidden">Print</span>
                </button>
                <button
                  onClick={() => {
                    track('calendar_share_clicked', {
                      persona: 'parent',
                      year: selectedYear,
                      children_count: children.length,
                    });
                    setIsShareModalOpen(true);
                  }}
                  className="btn btn-secondary inline-flex items-center gap-1.5 text-sm sm:text-base"
                >
                  <ArrowUpTrayIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                  Share
                </button>
              </>
            )}
            <Tooltip 
              content="Maximum of 5 children allowed"
              enabled={children.length >= 5}
            >
              <button
                onClick={() => setIsAddChildModalOpen(true)}
                className={`btn btn-secondary inline-flex items-center gap-1.5 text-sm sm:text-base ${
                  children.length >= 5 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : ''
                  }`}
                disabled={children.length >= 5}
              >
                <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                Add Child
              </button>
            </Tooltip>
          </div>
        </div>

        {childrenError && (
          <p className="text-red-500">
            Error loading children: {
              (childrenError ).message || 'An unknown error occurred'
            }
          </p>
        )}
        
        {/* Calendar Subscription Section */}
        {children.length > 0 && (
          <div className="mb-4 sm:mb-8">
            <CalendarSubscription />
          </div>
        )}
        
        {/* View Mode Toggle - Only show when there are children with schedules */}
        {(() => {
          const hasSchedules = filteredChildren.some(child => 
            child.camp_schedules && child.camp_schedules.length > 0
          );
          const hasScheduledMonths = Object.keys(schedulesByMonth).length > 0;
          
          if (!hasSchedules) return null;
          
          return (
            <div className="mb-4 sm:mb-6 flex justify-end">
              <div className="flex items-center gap-2 border border-primary rounded-lg p-1 bg-white">
                <button
                  onClick={() => setViewMode('child')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded transition-colors border ${
                    viewMode === 'child'
                      ? 'bg-primary/10 text-primary border-primary'
                      : 'bg-transparent text-gray-600 hover:text-gray-900 border-transparent'
                  }`}
                  title="Group by child"
                >
                  <ListBulletIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">By Child</span>
                </button>
                {/* Only show "By Month" if there are scheduled months */}
                {hasScheduledMonths && (
                  <button
                    onClick={() => setViewMode('month')}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded transition-colors border ${
                      viewMode === 'month'
                        ? 'bg-primary/10 text-primary border-primary'
                        : 'bg-transparent text-gray-600 hover:text-gray-900 border-transparent'
                    }`}
                    title="Group by month"
                  >
                    <Squares2X2Icon className="h-4 w-4" />
                    <span className="hidden sm:inline">By Month</span>
                  </button>
                )}
              </div>
            </div>
          );
        })()}
        
        {children.length > 0 ? (
          viewMode === 'month' && Object.keys(schedulesByMonth).length > 0 ? (
            // Month-grouped view - only show if there are schedules
            <div className="space-y-4 sm:space-y-8">
              {Object.keys(schedulesByMonth)
                .sort()
                .map(monthKey => {
                  const [year, month] = monthKey.split('-');
                  const monthName = new Date(parseInt(year), parseInt(month) - 1).toLocaleString('default', { month: 'long', year: 'numeric' });
                  const schedules = schedulesByMonth[monthKey];

                  // Get unique children for this month
                  const monthChildren = Array.from(new Set(
                    schedules.map(s => s.child_id).filter(Boolean)
                  )).map(childId => filteredChildren.find(c => c.id === childId)).filter(Boolean);

                  return (
                    <div key={monthKey} className="bg-white rounded-lg shadow overflow-hidden">
                      <div className="px-4 sm:px-6 lg:px-10 py-3 sm:py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                          <h3 className="text-xl font-semibold text-gray-900">{monthName}</h3>
                          {/* Children labels in upper right */}
                          {monthChildren.length > 0 && (
                            <div className="flex items-center gap-3 flex-wrap">
                              {monthChildren.map((child) => (
                                <div key={child.id} className="flex items-center gap-1.5">
                                  <div
                                    className="w-3 h-3 flex-shrink-0"
                                    style={{ backgroundColor: childColorMap.get(child.id) || CHILD_COLORS[0] }}
                                  />
                                  <span className="text-sm text-gray-600">{child.name}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="px-4 sm:px-6 lg:px-10 py-4 sm:py-6">
                        <div className="space-y-3">
                          {schedules.map(schedule => {
                            const child = filteredChildren.find(c => c.id === schedule.child_id);
                            const getCampUrl = () => {
                              if (schedule.camp?.slug) {
                                return `/camps/${schedule.camp.slug}`;
                              }
                              if (schedule.camp?.state && schedule.camp?.city && schedule.camp?.name) {
                                const stateSlug = schedule.camp.state.toLowerCase()
                                  .replace(/[^a-z0-9\s-]/g, '')
                                  .replace(/\s+/g, '-')
                                  .trim();
                                const citySlug = schedule.camp.city.toLowerCase()
                                  .replace(/[^a-z0-9\s-]/g, '')
                                  .replace(/\s+/g, '-')
                                  .trim();
                                const nameSlug = schedule.camp.name.toLowerCase()
                                  .replace(/[^a-z0-9\s-]/g, '')
                                  .replace(/\s+/g, '-')
                                  .trim();
                                return `/camps/${stateSlug}/${citySlug}/${nameSlug}`;
                              }
                              return '#';
                            };
                            const startDate = parseDateString(schedule.start_date);
                            const endDate = parseDateString(schedule.end_date);
                            const days = startDate && endDate ? Math.abs(daysBetween(schedule.end_date, schedule.start_date)) + 1 : 1;

                            return (
                              <div
                                key={schedule.id}
                                className="flex items-center gap-4 p-3 sm:p-4 border-l-4"
                                style={{ borderLeftColor: child ? childColorMap.get(child.id) || CHILD_COLORS[0] : CHILD_COLORS[0] }}
                              >
                                <div className="flex-1 max-w-4xl">
                                  <div className="flex items-center gap-2">
                                    {schedule.camp ? (
                                      <Link
                                        to={getCampUrl()}
                                        className="font-semibold text-primary hover:text-primary/80 transition-colors inline-flex items-center gap-1 group"
                                      >
                                        <span>{schedule.camp.name}</span>
                                        <ChevronRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                                      </Link>
                                    ) : (
                                      <span className="font-semibold text-gray-900">Unknown Camp</span>
                                    )}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditSchedule(schedule);
                                      }}
                                      className="text-primary hover:text-primary/80 transition-colors p-0.5 ml-1"
                                      title="Edit schedule"
                                    >
                                      <PencilSquareIcon className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => setDeleteConfirmation({
                                        scheduleId: schedule.id,
                                        childName: child?.name || schedule.child_name || 'Unknown',
                                        campName: schedule.camp?.name || 'Summer Camp',
                                        camp_id: schedule.camp?.id,
                                        child_id: schedule.child_id || child?.id,
                                      })}
                                      className="text-red-600 hover:text-red-900 transition-colors p-0.5"
                                      title="Delete schedule"
                                    >
                                      <XMarkIcon className="h-4 w-4" />
                                    </button>
                                  </div>
                                  <div className="text-sm text-gray-600 mt-1">
                                    {startDate && formatDateString(schedule.start_date, {
                                      month: 'short',
                                      day: 'numeric',
                                    })}
                                    {startDate && endDate && schedule.start_date !== schedule.end_date && (
                                      <>
                                        {' - '}
                                        {formatDateString(schedule.end_date, {
                                          month: 'short',
                                          day: 'numeric',
                                          year: getYearFromDateString(schedule.start_date) !== getYearFromDateString(schedule.end_date) ? 'numeric' : undefined,
                                        })}
                                      </>
                                    )}
                                    {days > 1 && (
                                      <span className="text-gray-500 ml-2">({days} days)</span>
                                    )}
                                  </div>
                                  {schedule.camp && (schedule.camp.address || (schedule.camp.city && schedule.camp.state)) && (
                                    <div className="text-xs text-gray-500 mt-1">
                                      <a
                                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([schedule.camp.address, schedule.camp.city, schedule.camp.state, schedule.camp.zip_code].filter(Boolean).join(', '))}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary hover:text-primary/80 transition-colors inline-flex items-center gap-1 group no-underline"
                                      >
                                        <span>
                                          {schedule.camp.address 
                                            ? `${schedule.camp.address}${schedule.camp.city ? `, ${schedule.camp.city}` : ''}${schedule.camp.state ? `, ${schedule.camp.state}` : ''}${schedule.camp.zip_code ? ` ${schedule.camp.zip_code}` : ''}`
                                            : schedule.camp.city && schedule.camp.state
                                              ? `${schedule.camp.city}, ${schedule.camp.state}`
                                              : schedule.camp.address || ''
                                          }
                                        </span>
                                        <ArrowTopRightOnSquareIcon className="h-3 w-3 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                      </a>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            // Child-grouped view (original) - always show all children, even without schedules
            <div className="space-y-4 sm:space-y-8">
              {filteredChildren.map((child) => (
              <div key={child.id} className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-4 sm:px-6 lg:px-10 py-3 sm:py-4 border-b border-gray-200">
                  <div className="flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-gray-900 leading-tight">
                      {child.name} <span className="text-gray-500 text-sm">({formatAge(calculateCurrentAge(child.date_of_birth))})</span>
                    </h2>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => {
                          // Ensure all required properties are present and correctly typed
                          if (child.id && child.name && child.date_of_birth) {
                            setSelectedChild({
                              id: child.id,
                              name: child.name,
                              date_of_birth: child.date_of_birth
                            });
                            setIsEditModalOpen(true);
                          }
                        }}
                        className="btn btn-secondary inline-flex items-center gap-1.5 text-sm sm:text-base"
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          // Ensure child.id is defined before using it
                          if (child.id) {
                            setItemToDelete({ type: 'child', id: child.id });
                            setIsDeleteConfirmationOpen(true);
                          }
                        }}
                        className="btn inline-flex items-center gap-1.5 text-sm sm:text-base text-red-600 bg-transparent border border-red-600 hover:bg-red-50"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
                <div className="px-4 sm:px-6 lg:px-10 py-3 sm:py-4">
                  {child.camp_schedules && child.camp_schedules.length > 0 ? (
                    <ul className="divide-y divide-gray-200">
                      {child.camp_schedules.map((schedule) => {
                        // Construct camp URL - use slug if available, otherwise build from state/city/name
                        const getCampUrl = () => {
                          if (schedule.camp?.slug) {
                            return `/camps/${schedule.camp.slug}`;
                          }
                          if (schedule.camp?.state && schedule.camp?.city && schedule.camp?.name) {
                            const stateSlug = schedule.camp.state.toLowerCase()
                              .replace(/[^a-z0-9\s-]/g, '')
                              .replace(/\s+/g, '-')
                              .trim();
                            const citySlug = schedule.camp.city.toLowerCase()
                              .replace(/[^a-z0-9\s-]/g, '')
                              .replace(/\s+/g, '-')
                              .trim();
                            const nameSlug = schedule.camp.name.toLowerCase()
                              .replace(/[^a-z0-9\s-]/g, '')
                              .replace(/\s+/g, '-')
                              .trim();
                            return `/camps/${stateSlug}/${citySlug}/${nameSlug}`;
                          }
                          return '#';
                        };

                        return (
                          <li key={schedule.id} className="py-6">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <Link
                                  to={getCampUrl()}
                                  className="text-lg font-semibold text-primary hover:text-primary/80 transition-colors cursor-pointer inline-flex items-center gap-1 group"
                                >
                                  <span>{schedule.camp?.name || 'Unknown Camp'}</span>
                                  <ChevronRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                                </Link>
                                <div className="flex items-center gap-2 mt-1">
                                  <p className="text-base font-medium text-gray-900">
                                    {formatDateString(schedule.start_date)} - {formatDateString(schedule.end_date)}
                                  </p>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handleEditSchedule(schedule);
                                    }}
                                    className="text-primary hover:text-primary/80 transition-colors p-0.5"
                                    title="Edit schedule"
                                  >
                                    <PencilSquareIcon className="h-4 w-4" />
                                  </button>
                                </div>
                                {schedule.camp && (schedule.camp.address || (schedule.camp.city && schedule.camp.state)) && (
                                  <div className="mt-2">
                                    <a
                                      href={
                                        schedule.camp.latitude && schedule.camp.longitude
                                          ? `https://www.google.com/maps/search/?api=1&query=${schedule.camp.latitude},${schedule.camp.longitude}`
                                          : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([schedule.camp.address, schedule.camp.city, schedule.camp.state, schedule.camp.zip_code].filter(Boolean).join(', '))}`
                                      }
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-sm text-primary hover:text-primary/80 transition-colors inline-flex items-center gap-1.5 group"
                                    >
                                      <span>
                                        {schedule.camp.address 
                                          ? `${schedule.camp.address}${schedule.camp.city ? `, ${schedule.camp.city}` : ''}${schedule.camp.state ? `, ${schedule.camp.state}` : ''}${schedule.camp.zip_code ? ` ${schedule.camp.zip_code}` : ''}`
                                          : schedule.camp.city && schedule.camp.state
                                            ? `${schedule.camp.city}, ${schedule.camp.state}`
                                            : schedule.camp.address || ''
                                        }
                                      </span>
                                      <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                    </a>
                                  </div>
                                )}
                              <div className="mt-2">
                                <CalendarExport
                                  childName={child.name}
                                  campName={schedule.camp?.name || 'Summer Camp'}
                                  startDate={schedule.start_date}
                                  endDate={schedule.end_date}
                                  address={schedule.camp?.address}
                                  city={schedule.camp?.city}
                                  state={schedule.camp?.state}
                                  zipCode={schedule.camp?.zip_code}
                                />
                              </div>
                            </div>
                            <button
                              onClick={() => setDeleteConfirmation({
                                scheduleId: schedule.id,
                                childName: child.name,
                                campName: schedule.camp?.name || 'Summer Camp',
                                camp_id: schedule.camp?.id,
                                child_id: schedule.child_id || child.id,
                              })}
                              className="text-sm text-red-600 hover:text-red-900"
                            >
                              <XMarkIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500">
                      {isFetching ? 'Loading schedules...' : 'No camps scheduled'}
                    </p>
                  )}
                </div>

                <div className="px-4 sm:px-6 lg:px-10 py-4 sm:py-6 border-t border-gray-200 flex justify-between items-center">
                  <button
                    onClick={() => {
                      setIsAddScheduleModalOpen(true);
                      setSelectedChildId(child.id);
                    }}
                    className="btn btn-secondary inline-flex items-center gap-1.5 text-sm sm:text-base"
                  >
                    <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                    Add Camp
                  </button>
                  
                  {/* Bulk Calendar Export - Only show if this child has camps for selected year */}
                  {(() => {
                    // Check if this child has any camps scheduled for the selected year
                    // Show all children, even if they don't have schedules yet
                    
                    // Flatten all schedules from all children for bulk export
                    const allSchedules = children.flatMap(c => 
                      (c.camp_schedules || []).map(s => ({
                        id: s.id,
                        start_date: s.start_date,
                        end_date: s.end_date,
                        event_year: s.event_year,
                        child: {
                          id: c.id,
                          name: c.name
                        },
                        camp: {
                          id: s.camp?.id || '',
                          name: s.camp?.name || 'Unknown Camp',
                          description: s.camp?.description || null,
                          address: s.camp?.address || null,
                          city: s.camp?.city || null,
                          state: s.camp?.state || null,
                          zip_code: s.camp?.zip_code || null
                        }
                      }))
                    );
                    
                    return (
                      <button
                        onClick={() => {
                          track('calendar_ics_downloaded', {
                            persona: 'parent',
                            scope: 'all_for_child',
                            child_id: child.id,
                            child_name: child.name,
                            year: selectedYear,
                            schedule_count: allSchedules.length,
                          });
                          generateBulkICSFile(allSchedules, selectedYear);
                        }}
                        className="text-sm text-primary hover:text-primary/80 inline-flex items-center gap-1.5 transition-colors"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4" />
                        Download all calendar files for {child.name} (.ics)
                      </button>
                    );
                  })()}
                </div>
              </div>
            ))}
            
          </div>
        )
        ) : (
          <div className="text-center py-16 sm:py-20 bg-white rounded-lg shadow">
            {/* Planner icon above content */}
            <div className="flex justify-center mb-6">
              <ClipboardDocumentListIcon className="h-24 w-24 sm:h-32 sm:w-32 text-primary/10" />
            </div>
            
            {/* Content */}
            <div>
              <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-3">
                Start planning your camp season
              </h2>
              <p className="text-base sm:text-lg text-gray-600 mb-6 max-w-md mx-auto">
                Add your first child to begin building your family's camp schedule. Plan smarter, stress less.
              </p>
              <button
                onClick={() => setIsAddChildModalOpen(true)}
                className={`btn btn-primary inline-flex items-center gap-1.5 text-sm sm:text-base ${
                  children.length >= 5 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : ''
                  }`}
                disabled={children.length >= 5}
              >
                <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                Add Your First Child
              </button>
            </div>
          </div>
        )}
        </div>

        <AddChildModal
          isOpen={isAddChildModalOpen}
          onClose={() => setIsAddChildModalOpen(false)}
          onAdd={handleAddChild}
        />

        <CalendarShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          selectedYear={selectedYear}
          childrenCount={children.length}
        />
        
        <CalendarPreviewModal
          isOpen={isPreviewModalOpen}
          onClose={() => setIsPreviewModalOpen(false)}
          children={filteredChildren}
          selectedYear={selectedYear}
          userId={session?.user?.id}
        />

        <AddToCalendarModal
          isOpen={isAddScheduleModalOpen}
          onClose={() => {
            setIsAddScheduleModalOpen(false);
            setEditingSchedule(null);
            setSelectedChildId(null);
          }}
          onAdd={handleAddSchedule}
          childId={selectedChildId || ''}
          childName={children.find(child => child.id === selectedChildId)?.name || ''}
          editingSchedule={editingSchedule}
        />

        {/* Delete Confirmation Dialog */}
        <Transition.Root show={!!deleteConfirmation} as={Fragment}>
          <Dialog as="div" className="relative z-[9999]" onClose={() => setDeleteConfirmation(null)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-500/75 backdrop-blur-sm transition-opacity" />
            </Transition.Child>

            <div className="fixed inset-0 z-10 overflow-y-auto">
              <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                  <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                    <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                      <div className="sm:flex sm:items-start">
                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                          <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                        </div>
                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                          <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                            Remove Camp Schedule
                          </Dialog.Title>
                          <div className="mt-2">
                            <p className="text-sm text-gray-500">
                              Are you sure you want to remove {deleteConfirmation?.campName} from {deleteConfirmation?.childName}'s schedule?{' '}
                              <span className="font-semibold text-gray-700">This action cannot be undone.</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                      <button
                        type="button"
                        className="btn-danger sm:ml-3 sm:w-auto"
                        onClick={handleRemoveSchedule}
                      >
                        Remove
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary mt-3 sm:mt-0 sm:w-auto"
                        onClick={() => setDeleteConfirmation(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition.Root>

        {/* Edit Modal */}
        <Transition.Root show={isEditModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-[9999]" onClose={() => setIsEditModalOpen(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-500/75 backdrop-blur-sm transition-opacity" />
            </Transition.Child>

            <div className="fixed inset-0 z-10 overflow-y-auto">
              <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                  <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                    <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                      <div className="absolute right-0 top-0 pr-4 pt-4">
                        <button
                          type="button"
                          className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                          onClick={() => setIsEditModalOpen(false)}
                        >
                          <span className="sr-only">Close</span>
                          <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                        </button>
                      </div>

                      <div className="space-y-6">
                        <div>
                          <h3 className="text-lg font-semibold leading-6 text-gray-900">
                            Edit Child
                          </h3>
                        </div>

                        <div className="space-y-4">
                          <div>
                            <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700">
                              Child's Name
                            </label>
                            <input
                              type="text"
                              id="edit-name"
                              value={selectedChild?.name || ''}
                              onChange={(e) => {
                                if (selectedChild) {
                                  setSelectedChild({
                                    ...selectedChild,
                                    name: e.target.value
                                  });
                                }
                              }}
                              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                              required
                              minLength={2}
                              maxLength={50}
                            />
                          </div>

                          <div>
                            <label htmlFor="edit-date_of_birth" className="block text-sm font-medium text-gray-700">
                              Date of Birth
                            </label>
                            <input
                              type="date"
                              id="edit-date_of_birth"
                              value={selectedChild?.date_of_birth || ''}
                              onChange={(e) => {
                                if (selectedChild) {
                                  setSelectedChild({
                                    ...selectedChild,
                                    date_of_birth: e.target.value
                                  });
                                }
                              }}
                              max={new Date().toISOString().split('T')[0]} // Cannot be in the future
                              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary date-picker-primary"
                              required
                            />
                            {selectedChild?.date_of_birth && (
                              <p className="mt-1 text-xs text-gray-500">
                                Age: {formatAge(calculateCurrentAge(selectedChild.date_of_birth))}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                      <button
                        type="button"
                        className="btn btn-primary sm:ml-3 sm:w-auto"
                        onClick={handleEditChild}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary mt-3 sm:mt-0 sm:w-auto"
                        onClick={() => setIsEditModalOpen(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition.Root>

        <Transition.Root show={isDeleteConfirmationOpen} as={Fragment}>
          <Dialog as="div" className="relative z-[9999]" onClose={() => setIsDeleteConfirmationOpen(false)}>
            <div className="fixed inset-0 bg-gray-500/75 backdrop-blur-sm transition-opacity" />
            <div className="fixed inset-0 z-10 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                  <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                    <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                      <h3 className="text-lg font-semibold">Are you sure?</h3>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Are you sure you want to delete this child? This action cannot be undone.
                        </p>
                      </div>
                    </div>
                    <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                      <button
                        type="button"
                        className="btn-danger sm:ml-3 sm:w-auto"
                        onClick={() => {
                          if (itemToDelete) {
                            handleDeleteChild(itemToDelete.id);
                          }
                          setIsDeleteConfirmationOpen(false);
                        }}
                      >
                        Delete
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary mt-3 sm:mt-0 sm:w-auto"
                        onClick={() => setIsDeleteConfirmationOpen(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition.Root>
      </div>
    </>
  );
}